import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UtensilsCrossed, ClipboardList, Store, Truck, PackageCheck, CalendarClock,
  ListChecks, Settings, ExternalLink, Bell, Users,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../api";
import { getCached, setCached } from "../../lib/dataCache";
import { createVisiblePoller } from "../lib/visiblePoller";
import SellerWorkspaceMenu from "../components/SellerWorkspaceMenu";
import SellerCommunicationBanner from "../components/SellerCommunicationBanner";

/**
 * VendorLayout — seller business workspace shell.
 *
 * Business-first navigation (not customer-shopping-with-tools). Layout is
 * fetch-and-cache authority for /vendors/me + menu + orders + earnings;
 * child routes read via `useOutletContext()`.
 *
 * Storefront is the seller landing page. Orders owns the nested Prep Board;
 * optional preorder tools live under More instead of crowding the primary nav.
 */

const NAV_PRIMARY = [
  { to: "/vendor/storefront", label: "Storefront", icon: Store, testid: "vnav-storefront" },
  { to: "/vendor/orders", label: "Orders", icon: ClipboardList, testid: "vnav-orders" },
  { to: "/vendor/production", label: "Prep Board", icon: PackageCheck, testid: "vnav-production", subItem: true },
  { to: "/vendor/menu", label: "Menu", icon: UtensilsCrossed, testid: "vnav-menu" },
  { to: "/vendor/delivery", label: "Fulfillment", icon: Truck, testid: "vnav-delivery" },
  { to: "/vendor/growth", label: "Growth", icon: Users, testid: "vnav-growth" },
];

const NAV_MORE = [
  { to: "/vendor/drops", label: "Preorder Drops", icon: CalendarClock, testid: "vnav-drops" },
];

const NAV_SECONDARY = [
  { to: "/vendor/notifications", label: "Notifications", icon: Bell, testid: "vnav-alerts", dynamicBadge: "notifications" },
  { to: "/vendor/account", label: "Profile & Settings", icon: Settings, testid: "vnav-account" },
];

const CACHE_VENDOR = "vendor:me";
const CACHE_MENU = "vendor:menu";
const CACHE_ORDERS = "vendor:orders";
const CACHE_EARNINGS = "vendor:earnings";

export default function VendorLayout() {
  const { user } = useAuth();
  const userId = user?.id || null;
  const userRole = user?.role || null;
  const nav = useNavigate();

  useEffect(() => {
    if (user && user.role !== "vendor") nav("/", { replace: true });
  }, [user, nav]);

  const cachedVendor = getCached(CACHE_VENDOR);
  const [vendor, setVendor] = useState(() => cachedVendor ?? null);
  const [menu, setMenu] = useState(() => getCached(CACHE_MENU) ?? []);
  const [orders, setOrders] = useState(() => getCached(CACHE_ORDERS) ?? []);
  const [earnings, setEarnings] = useState(() => getCached(CACHE_EARNINGS) ?? null);
  const [loaded, setLoaded] = useState(() => cachedVendor !== undefined);
  const [noProfile, setNoProfile] = useState(false);
  const [notificationUnread, setNotificationUnread] = useState(0);

  const loadVendor = useCallback(async () => {
    const cached = getCached(CACHE_VENDOR);
    const cachedVendorId = cached?.id || null;
    const vendorRequest = api.get("/vendors/me");
    // Orders and earnings do not depend on the vendor payload, so start them
    // immediately instead of serializing them behind /vendors/me.
    const ordersRequest = api.get("/orders/vendor");
    const earningsRequest = api.get("/vendors/me/earnings");
    const cachedMenuRequest = cachedVendorId
      ? api.get(`/vendors/${cachedVendorId}/menu`)
      : null;

    try {
      const { data } = await vendorRequest;
      setVendor(data);
      setCached(CACHE_VENDOR, data);
      setNoProfile(false);
      setLoaded(true);

      const menuRequest = cachedVendorId === data.id && cachedMenuRequest
        ? cachedMenuRequest
        : api.get(`/vendors/${data.id}/menu`);
      const [menuResult, ordersResult, earningsResult] = await Promise.allSettled([
        menuRequest,
        ordersRequest,
        earningsRequest,
      ]);
      if (menuResult.status === "fulfilled") {
        setMenu(menuResult.value.data);
        setCached(CACHE_MENU, menuResult.value.data);
      }
      if (ordersResult.status === "fulfilled") {
        setOrders(ordersResult.value.data);
        setCached(CACHE_ORDERS, ordersResult.value.data);
      }
      if (earningsResult.status === "fulfilled") {
        setEarnings(earningsResult.value.data);
        setCached(CACHE_EARNINGS, earningsResult.value.data);
      }
    } catch (e) {
      // Prevent intentionally started parallel requests from surfacing as
      // unhandled rejections when the vendor profile itself is unavailable.
      void ordersRequest.catch(() => {});
      void earningsRequest.catch(() => {});
      if (cachedMenuRequest) void cachedMenuRequest.catch(() => {});
      if (e?.response?.status === 404) {
        setVendor(null);
        setNoProfile(true);
      } else {
        toast.error(formatApiError(e));
      }
      setLoaded(true);
    }
  }, []);

  useEffect(() => { loadVendor(); }, [loadVendor]);

  useEffect(() => {
    const onNotificationsUpdated = (event) => {
      setNotificationUnread(Math.max(0, Number(event?.detail?.unread_count || 0)));
    };
    window.addEventListener("ap:seller-notifications-updated", onNotificationsUpdated);
    return () => window.removeEventListener("ap:seller-notifications-updated", onNotificationsUpdated);
  }, []);

  useEffect(() => {
    if (!userId || userRole !== "vendor") return undefined;
    let cancelled = false;
    const loadNotifications = async () => {
      try {
        const { data } = await api.get("/notifications/me", { params: { limit: 1 } });
        if (!cancelled) setNotificationUnread(Number(data?.unread_count || 0));
      } catch {
        // Alerts page remains available even if one background poll fails.
      }
    };
    // Notifications are background data. Give profile/menu/order requests
    // the first network slot so the workspace becomes usable sooner.
    let poller = null;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      void loadNotifications();
      poller = createVisiblePoller({ intervalMs: 60000, task: loadNotifications });
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      poller?.stop();
    };
  }, [userId, userRole]);

  const seenOrderIdsRef = useRef(new Set());
  useEffect(() => {
    if (!vendor) return undefined;
    if (seenOrderIdsRef.current.size === 0 && orders.length) {
      seenOrderIdsRef.current = new Set(orders.map((o) => o.id));
    }

    let cancelled = false;
    const poller = createVisiblePoller({
      intervalMs: 20000,
      task: async () => {
        try {
          const o = await api.get("/orders/vendor");
          if (cancelled) return;
          const fresh = o.data;
          const newOnes = fresh.filter(
            (x) => !seenOrderIdsRef.current.has(x.id) && x.status === "pending_vendor_acceptance"
          );
          newOnes.forEach((x) => {
            const who = x.is_guest ? `Guest: ${x.guest_name || "Anonymous"}` : "Customer";
            toast.success(`New order — ${who} · $${x.total.toFixed(2)}`, {
              description: `${x.items.length} item${x.items.length > 1 ? "s" : ""} · #${x.id.slice(0, 8)}`,
              duration: 8000,
            });
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain); gain.connect(ctx.destination);
              osc.frequency.value = 880; gain.gain.value = 0.05;
              osc.start(); osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
              osc.stop(ctx.currentTime + 0.3);
              window.setTimeout(() => { void ctx.close?.(); }, 400);
            } catch { /* audio API can fail before a user gesture — silent */ }
          });
          if (newOnes.length > 0) {
            const original = document.title;
            document.title = `🔔 (${newOnes.length}) ${original}`;
            setTimeout(() => { document.title = original; }, 4000);
          }
          fresh.forEach((x) => seenOrderIdsRef.current.add(x.id));
          setOrders(fresh);
          setCached(CACHE_ORDERS, fresh);
        } catch { /* background poll — transient errors intentionally silent */ }
      },
    });

    return () => {
      cancelled = true;
      poller.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor?.id]);

  const refreshOrders = useCallback(async () => {
    const o = await api.get("/orders/vendor");
    setOrders(o.data);
    setCached(CACHE_ORDERS, o.data);
  }, []);

  const refreshMenu = useCallback(async () => {
    if (!vendor) return;
    const m = await api.get(`/vendors/${vendor.id}/menu`);
    setMenu(m.data);
    setCached(CACHE_MENU, m.data);
  }, [vendor]);

  const updateVendor = useCallback((next) => {
    setVendor(next);
    setCached(CACHE_VENDOR, next);
  }, []);

  if (!user) return null;

  const ctx = { vendor, menu, orders, earnings, loaded, noProfile, reload: loadVendor, refreshOrders, refreshMenu, updateVendor };

  // Setup tab only appears until the vendor application is submitted.
  // Once the seller has a profile, Setup becomes clutter.
  const setupItem = { to: "/vendor/setup", label: "Application", icon: ListChecks, testid: "vnav-setup" };
  const showSetup = noProfile || (vendor && vendor.status !== "approved");

  return (
    <div className="min-h-[100dvh] flex flex-col md:flex-row bg-[#fffdf8]" data-testid="vendor-layout">
      {/* Sidebar (desktop) — Shopify-grade dense workspace nav */}
      <aside
        className="hidden h-[100dvh] w-60 shrink-0 flex-col border-r border-white/10 bg-[#0b0d0f] text-white md:sticky md:top-0 md:flex"
        data-testid="vendor-sidebar"
      >
        <div className="border-b border-white/10 px-5 py-5">
          <div className="flex items-center gap-3">
            {vendor?.image_url ? (
              <img
                src={vendor.image_url}
                alt=""
                aria-hidden="true"
                className="h-12 w-12 shrink-0 rounded-full border border-white/15 bg-white/5 object-cover"
              />
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-[#e89a6b]" aria-hidden="true">
                <Store size={21} strokeWidth={1.8} />
              </span>
            )}
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#d98552]">Kitchen workspace</div>
              <div className="mt-0.5 truncate font-serif text-lg font-semibold text-white" title={vendor?.business_name || "Your kitchen"}>
                {vendor?.business_name || "Your kitchen"}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
          <NavGroup label="Workspace" items={NAV_PRIMARY} badges={{ notifications: notificationUnread }} />
          {vendor?.status === "approved" && <MoreNav items={NAV_MORE} />}
          {showSetup && <NavGroup label="Setup" items={[setupItem]} />}
        </nav>

        <div className="space-y-1 border-t border-white/10 px-3 py-3">
          <NavGroup label="Account" items={NAV_SECONDARY} badges={{ notifications: notificationUnread }} />
          {vendor?.id && vendor.status === "approved" && vendor.is_public ? (
            <Link
              to={`/vendor/${vendor.id}`}
              data-testid="vnav-view-storefront"
              className="flex items-center gap-2 text-xs text-white/62 transition-colors hover:text-[#e89a6b]"
            >
              <ExternalLink size={12} /> View live storefront
            </Link>
          ) : (
            <Link
              to="/"
              data-testid="vnav-view-customer-home"
              className="flex items-center gap-2 text-xs text-white/62 transition-colors hover:text-[#e89a6b]"
            >
              <ExternalLink size={12} /> Back to customer site
            </Link>
          )}
        </div>
      </aside>

      <SellerWorkspaceMenu
        vendor={vendor}
        notificationUnread={notificationUnread}
        showSetup={showSetup}
      />

      <main className="min-w-0 flex-1 bg-[#fffdf8]" data-testid="vendor-main">
        {vendor && <SellerCommunicationBanner />}
        <Suspense
          fallback={
            <div
              className="min-h-[45vh] px-5 py-8"
              data-testid="vendor-route-loading"
              aria-busy="true"
              aria-label="Loading seller page"
            >
              <div className="mx-auto max-w-5xl animate-pulse space-y-4">
                <div className="h-7 w-44 rounded-lg bg-[#f3eee8]" />
                <div className="h-24 rounded-2xl bg-[#f7f2ec]" />
                <div className="h-24 rounded-2xl bg-[#f7f2ec]" />
              </div>
            </div>
          }
        >
          <Outlet context={ctx} />
        </Suspense>
      </main>
    </div>
  );
}

function NavGroup({ label, items, badges = {} }) {
  return (
    <div>
      <div className="px-3 pb-1.5 text-[11px] font-semibold text-white/55">{label}</div>
      <div className="space-y-0.5">
        {items.map(({ to, label: itemLabel, icon: Icon, testid, dynamicBadge, sectionLink, subItem }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/vendor/storefront" ? false : true}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg py-2 text-sm transition-colors ${subItem ? "ml-6 border-l border-white/10 pl-3 pr-2 text-xs " : "px-3 "}${
                isActive && !sectionLink
                  ? "bg-[#C25A2E]/16 text-[#f0a173] font-semibold ring-1 ring-inset ring-[#C25A2E]/30"
                  : "text-white/72 hover:bg-white/7 hover:text-white"
              }`
            }
          >
            <Icon size={15} strokeWidth={2} /> <span className="flex-1">{itemLabel}</span>
            {dynamicBadge && Number(badges[dynamicBadge] || 0) > 0 && (
              <span className="rounded-full bg-[var(--ap-primary)] px-2 py-0.5 text-[10px] font-bold text-white">{Number(badges[dynamicBadge]) > 99 ? "99+" : Number(badges[dynamicBadge])}</span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function MoreNav({ items }) {
  return (
    <details className="group">
      <summary
        className="cursor-pointer list-none px-3 py-2 text-xs font-semibold text-white/62 transition hover:text-white"
        data-testid="vnav-more"
      >
        More
      </summary>
      <div className="mt-1 border-l border-white/10 pl-3">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors ${
                isActive
                  ? "bg-[#C25A2E]/16 font-semibold text-[#f0a173]"
                  : "text-white/68 hover:bg-white/7 hover:text-white"
              }`
            }
          >
            <Icon size={14} strokeWidth={2} />
            <span>{label}</span>
          </NavLink>
        ))}
      </div>
    </details>
  );
}

